/**
 * Block letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blockLettersPack: ToilePack = {
    slug: 'block-letters',
    category: 'illustration',
    name: 'Block letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'alphabet', label: 'Full alphabet', subject: 'a chart of the 26 capitals "A" to "Z" as thick square block outlines in five rows, straight sides, empty inside'},
        {key: 'cutout', label: 'Cut-out letters', subject: 'four large block outline capitals "A", "B", "C" and "D" in a two-by-two grid filling the page, each with a dashed cut line around it'},
        {key: 'banner', label: 'Banner word', subject: 'the word "WELCOME" in tall block outline capitals running across the page, evenly spaced, each letter empty for coloring'},
    ],
    styles: [
        {key: 'square', label: 'Square outline', style: 'thick square block capitals, one even outline, straight sides and sharp corners, empty inside', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'shadow', label: 'Offset shadow', style: 'thick square block capitals with a second outline offset down and to the right to give a solid drop-shadow edge', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'rounded', label: 'Rounded corners', style: 'thick block capitals with softly rounded corners and one even bold outline, empty inside', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['a name', 'numbers', 'small letters'],
    directionHint: 'try: a name',
    seo: {
        title: 'Block Letters: Alphabet, Cut-Out, Banner Word',
        metaDescription: 'Nine printable block letter sheets for posters and signs: an A to Z chart, large cut-out letters and a banner word.',
        h1: 'Block letters: full alphabet, cut-out letters, a banner word',
        tagline: 'Thick square outline letters in three styles, wide enough to trace, cut or color.',
        intro: 'Block letters are plain, thick, square outline letters for posters, signs, bulletin boards and school projects. The nine sheets give a full A to Z chart in five rows, four large capitals to cut out, and the word WELCOME as a banner across the page. Each comes as a square outline, with an offset shadow, or with rounded corners. Ask for a name, numbers or small letters and the same thick block outline is drawn in that form.',
        useCases: [
            'a classroom bulletin board title',
            'a poster or a shop sign',
            'letters to cut out for a school project',
            'a welcome banner',
        ],
        faq: [
            {q: 'Are these printable?', a: 'Each sheet is black outline on a plain white background, with the letters empty inside.'},
            {q: 'What does the cut-out sheet show?', a: 'Four large capitals, A to D, in a two-by-two grid that fills the page.'},
            {q: 'What is the offset shadow style?', a: 'A second outline set down and to the right, giving the letters a solid edge like a drop shadow.'},
            {q: 'Can I get a name instead of a word?', a: 'Yes. Ask for a name, numbers or small letters, and the same block outline is drawn in that form.'},
        ],
    },
}
